// react
import { useRef, useState } from 'react'

// external libs
import { Box, IconButton, makeStyles, Paper, Typography } from '@material-ui/core'
import { DeleteOutlineOutlined, EditOutlined } from '@material-ui/icons'

// components
import ChecklistItemForm from './ChecklistItemForm'
import Dialog from '../../../control/dialog/dialog'

// hooks
import { useChecklistContext } from './ChecklistList'
import useAuthenStore from '../../../stores/authen/authenStore'
import useChecklist from '../../../services/um/useChecklist'
import useLoading from '../../../control/loading/useLoading'
import useTranslate from '../../../i18n/useTranslate'

// types
import type { DialogType } from '../../../control/dialog/dialog'
import type { ChecklistAnswer, ChecklistRequest, DeleteChecklistRequest } from '../../../services/um/useChecklist'

interface ChecklistAnswerItemProps {
    topicID: number
    parentID: number
    childrenID?: number | undefined
    checklistAnswer: ChecklistAnswer
    onChange?: () => void
}

function ChecklistAnswerItem(props: ChecklistAnswerItemProps) {
    const { topicID, parentID, childrenID, checklistAnswer, onChange } = props
    const classes = useStyles()
    const translate = useTranslate()
    const currentLanguage = translate.languageStore.currentLanguage
    const loadingHook = useLoading()
    const checklistServices = useChecklist()
    const { profile } = useAuthenStore()
    const irb = Number(profile?.irb)
    const { setEditingChecklistId, hasEditingChecklist } = useChecklistContext()

    const [mode, setMode] = useState<'view' | 'edit'>('view')
    const dialogRef = useRef<Record<string, Function>>()
    const [dialogInfo, setDialogInfo] = useState<Partial<DialogType>>()

    const initialValues: ChecklistRequest = {
        topicID,
        irb,
        parentID,
        childrenID,
        isAnswer: true,
        nameTH: checklistAnswer.nameTH,
        nameEN: checklistAnswer.nameEN,
        isDefault: checklistAnswer.isDefault,
        checklistID: checklistAnswer.checklistID,
    }

    async function updateChecklist(values: ChecklistRequest) {
        try {
            dialogRef.current?.closeDialog()
            loadingHook.loadingOpen()

            const { status } = await checklistServices.updateChecklist(topicID, values)

            if (status !== 1) {
                throw new Error()
            }

            setDialogInfo({
                title: translate.get('ALERT.TITLE.NOTIFICATION'),
                content: translate.get('ALERT.CONTENT.SAVE_SUCCESS'),
                type: 'alert-message',
                onAfterClose: () => onChange && onChange(),
            })

            setEditingChecklistId(null)
            setMode('view')
        } catch (error) {
            setDialogInfo({
                title: translate.get('ALERT.TITLE.NOTIFICATION'),
                content: translate.get('ALERT.CONTENT.SAVE_NOT_SUCCESS'),
                type: 'alert-message',
            })
        } finally {
            loadingHook.loadingClose()
            dialogRef.current?.openDialog()
        }
    }

    function onEdit() {
        setEditingChecklistId(checklistAnswer.checklistID)
        setMode('edit')
    }

    async function deleteChecklistAnswer() {
        try {
            loadingHook.loadingOpen()

            const payload: DeleteChecklistRequest = {
                checklistID: checklistAnswer.checklistID,
                parentID,
                childrenID,
                isAnswer: true,
            }
            const { status } = await checklistServices.deleteChecklist(topicID, payload)

            if (status !== 1) {
                throw new Error()
            }

            setDialogInfo({
                title: translate.get('ALERT.TITLE.NOTIFICATION'),
                content: translate.get('ALERT.CONTENT.DELETE_SUCCESS'),
                type: 'alert-message',
                onAfterClose: () => onChange && onChange(),
            })
        } catch (error) {
            setDialogInfo({
                title: translate.get('ALERT.TITLE.NOTIFICATION'),
                content: translate.get('ALERT.CONTENT.DELETE_UNSUCCESS'),
                type: 'alert-message',
            })
        } finally {
            loadingHook.loadingClose()
            dialogRef.current?.openDialog()
        }
    }

    function onDelete() {
        setDialogInfo({
            title: translate.get('ALERT.TITLE.NOTIFICATION'),
            content: translate.get('ALERT.CONTENT.NEED_TO_DELETE'),
            type: 'alert-confirm',
            onClickConfirm: () => {
                dialogRef.current?.closeDialog()
                deleteChecklistAnswer()
            },
        })
        dialogRef.current?.openDialog()
    }

    function onCancel() {
        setEditingChecklistId(null)
        setMode('view')
    }

    return (
        <>
            <Paper variant="outlined" className={classes.paper}>
                <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                    <Typography className={classes.headline} noWrap variant="body2">
                        {translate.get('LABEL.TOPIC_ANSWER')}
                    </Typography>
                    {checklistAnswer.isDefault ? (
                        <Typography variant="caption" className={classes.defaultBadge}>
                            default
                        </Typography>
                    ) : null}
                </Box>

                {/* Answer Preview */}
                {mode === 'view' ? (
                    <Box className={classes.responsiveFlex} justifyContent="space-between">
                        <Typography className={classes.checklistName} variant="body2">
                            {checklistAnswer[`name${currentLanguage.toUpperCase()}` as keyof ChecklistAnswer]}
                        </Typography>

                        {/* Hide actions if there is a checklist during editing. */}
                        {!hasEditingChecklist ? (
                            <Box display="flex" ml="auto" alignItems="flex-end" flexShrink={0} style={{ gap: 16 }}>
                                <IconButton aria-label="edit" color="default" onClick={onEdit}>
                                    <EditOutlined />
                                </IconButton>
                                <IconButton aria-label="delete" color="secondary" onClick={onDelete}>
                                    <DeleteOutlineOutlined />
                                </IconButton>
                            </Box>
                        ) : null}
                    </Box>
                ) : null}

                {/*  Answer Form */}
                {mode === 'edit' ? (
                    <ChecklistItemForm initialValues={initialValues} onSubmit={updateChecklist} onCancel={onCancel} />
                ) : null}
            </Paper>

            <Dialog onRef={(funcs) => (dialogRef.current = funcs)} {...dialogInfo} />
        </>
    )
}

const useStyles = makeStyles((theme) => ({
    responsiveFlex: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
        },
    },

    paper: {
        padding: 24,
        transition: '0.3s',
        borderRadius: '4px',
        position: 'relative',
        '&:hover': {
            border: `1px solid ${theme.palette.primary.main}`,
        },
    },

    headline: {
        border: 'unset',
        fontWeight: 'bolder',
        fontSize: '16px',
        color: theme.palette.grey[50],
        padding: '5px',
    },

    checklistName: {
        fontWeight: 'lighter',
        fontSize: '20px',
        padding: '5px',
    },

    defaultBadge: {
        borderRadius: 20,
        paddingInline: 6,
        paddingBlock: 2,
        color: 'oklch(0.6856 0.1802 151.91)',
        backgroundColor: 'oklch(0.9819 0.0181 155.83)',
    },
}))

export default ChecklistAnswerItem
